import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import { Typography } from 'ui/typography';
import { Loader } from 'ui/loader';

import ReportAssignmentHeader from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/report-assignment-header';
import ReportAssignmentRow from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/report-assignment-row';
import useTableHeight from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/hooks/use-table-height';
import useReportData from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/hooks/use-report-data';
import useReportUsers from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/hooks/use-report-users';
import useAccessActions from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/hooks/use-access-actions';

import { useCurrentUser } from 'hooks/api/use-current-user';

interface ReportAssignmentProps {
  clientId: string | number | undefined;
  productId: string | number | undefined;
  parentLoading?: boolean;
}

const ReportAssignment = ({
  clientId,
  productId,
  parentLoading = false,
}: ReportAssignmentProps): React.JSX.Element => {
  const { reports, setReports, users, loading, setLoading, userId } =
    useReportData(clientId, productId);

  const { reportUsers, setReportUsers, fetchReportUsers } = useReportUsers(
    users,
    loading,
    setLoading,
    userId,
    clientId
  );

  const {
    handleReportAccessChange,
    handleDataIssueChange,
    handleAutoAssignChange,
    handleUserAccessChange,
  } = useAccessActions(
    reports,
    setReports,
    reportUsers,
    setReportUsers,
    userId,
    clientId,
    loading,
    setLoading
  );

  const { tableHeight, tableContainerRef } = useTableHeight([reports.length]);
  const currentUser = useCurrentUser();
  const currentUserData = currentUser?.data as
    | {
        internalAdmin?: boolean;
        reportAdmin?: boolean;
      }
    | null
    | undefined;
  const isInternalAdmin =
    !!currentUserData?.internalAdmin && !currentUserData?.reportAdmin;

  return (
    <ResponsiveWrapper customClass="relative">
      {!parentLoading && (
        <Loader loading={loading} backdrop="transparent" fullScreen={false} />
      )}

      <Box sx={{ my: 2.5 }}>
        <Typography data-testid="report-assignment-heading" variant="h3">
          Reports Assignment Settings
        </Typography>
      </Box>

      <Stack>
        <ReportAssignmentHeader />

        <div
          ref={tableContainerRef}
          className="overflow-y-auto custom-scroll flex-grow mt-4 pr-1.5"
          style={{ maxHeight: tableHeight }}
        >
          {reports.length === 0 ? (
            <Box
              sx={{
                p: 3,
                textAlign: 'center',
                color: 'tokens.gray.600',
                borderWidth: '1px',
                borderColor: 'tokens.navy.200',
                borderRadius: '0.375rem',
              }}
            >
              No reports linked yet.
            </Box>
          ) : (
            <Box
              sx={{
                borderWidth: '1px',
                borderRadius: '0.375rem',
                borderColor: 'tokens.navy.200',
                overflow: 'hidden',
              }}
            >
              <table className="w-full border-collapse">
                <tbody>
                  {reports.map((report, index) => (
                    <ReportAssignmentRow
                      key={report.id}
                      report={report}
                      fetchReportUsers={fetchReportUsers}
                      onReportAccessChange={handleReportAccessChange}
                      onDataIssueChange={handleDataIssueChange}
                      onAutoAssignChange={handleAutoAssignChange}
                      onUserAccessChange={handleUserAccessChange}
                      reportUsers={reportUsers[report.id]}
                      isFirst={index === 0}
                      isLast={index === reports.length - 1}
                      isLoading={loading}
                      isInternalAdmin={isInternalAdmin}
                    />
                  ))}
                </tbody>
              </table>
            </Box>
          )}
        </div>
      </Stack>
    </ResponsiveWrapper>
  );
};

export default ReportAssignment;
